import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  AlertTriangle,
  BadgePercent,
  Building2,
  ChevronDown,
  FileCheck2,
  Landmark,
  ListChecks,
  TrendingUp,
  WalletCards,
} from "lucide-react";
import { FormProvider, useFormContext, useWatch } from "react-hook-form";

import { AgreementsGroup } from "../orderForm/components/AgreementsGroup";
import { BuyOrderRow } from "../orderForm/components/BuyOrders";
import { OrdreModulSection } from "../orderForm/components/OrdreModulSection";
import { SellOrderRow } from "../orderForm/components/SellOrders";
import { useOrderForm } from "../orderForm/hooks/useOrderForm";
import {
  COMPANY_ALLOWED_PORT_TYPES,
  getManagementFeeOptions,
  getModelOptions,
  portfolioTypeOptions,
} from "../orderForm/OrderForm.options";
import { validateAccountNumber } from "../orderForm/OrderForm.rules";
import { OrderFormData } from "../orderForm/OrderForm.schema";
import { QueryModelPosition, QuerySecurity } from "@/api/queries/types";
import { Portfolio } from "@/api/types";
import { PRICELIST_ROWS } from "@/components/Pricelist/Pricelist";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/utils/number";

// Returns the equity fraction [0–1] for a given model type code.
// Fallback used when modelsData is not yet loaded.
function getModelEquityFractionFallback(mt: string): number {
  if (!mt || mt === "NOMODEL") return 0;
  if (mt.includes("RENTER")) return 0;
  if (mt.includes("TRYGG")) return 0.25;
  if (mt.includes("EIENDOM") || mt.includes("EKB") || mt.includes("AKSJER"))
    return 1;
  const m = mt.match(/(\d+)/);
  if (m) return Math.min(Number(m[1]), 100) / 100;
  return 0;
}

const NON_STOCK = new Set(["L", "ALT", "R", "K", "PF"]);
function isEquityByClass1(code: string): boolean {
  return !!code && !NON_STOCK.has(code);
}

// Model positions use numeric class_type1 IDs: "1" = fixed income, "12" = other non-stock
const NON_STOCK_MODEL = new Set(["1", "12"]);
function isEquityByModelClass1(code: string): boolean {
  return !NON_STOCK_MODEL.has(String(code ?? ""));
}

function getMinimumMandateAmount(modelType: string | undefined): number {
  if (modelType === "CAPGUARDEKB") return 1_000_000;
  if (modelType === "CAPGUARDEIENDOM") return 750_000;
  return 0;
}

// ─── Shared field styles ──────────────────────────────────────────────────────
const fieldCls =
  "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400";
const readonlyCls =
  "w-full cursor-default rounded-lg border border-border/40 bg-muted/40 px-3 py-2 text-sm text-muted-foreground";
const errorCls = "text-[11px] text-destructive mt-0.5";

function fmtAmt(n: number) {
  return n.toLocaleString("nb-NO", { maximumFractionDigits: 0 });
}

function FormSection({
  number,
  icon: Icon,
  title,
  description,
  children,
  className,
  disabled = false,
  collapsed = false,
}: {
  number: number;
  icon: React.ElementType;
  title: string;
  description: string;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  collapsed?: boolean;
}) {
  const isCollapsed = disabled || collapsed;

  return (
    <section
      className={cn(
        "overflow-hidden rounded-xl border border-border/70 bg-card shadow-[0_1px_3px_rgba(15,23,42,0.04)]",
        disabled && "opacity-70",
        className
      )}
    >
      <div
        className={cn(
          "flex items-start gap-3 border-b border-border/50 px-4 py-3",
          isCollapsed && "border-b-0"
        )}
      >
        <div
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-300",
            disabled && "bg-muted text-muted-foreground dark:bg-muted/40"
          )}
        >
          <span className="text-xs font-bold tabular-nums">{number}</span>
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon
              className={cn(
                "size-4 text-blue-600 dark:text-blue-400",
                disabled && "text-muted-foreground"
              )}
            />
            <h2 className="text-sm font-semibold leading-tight text-foreground">
              {title}
            </h2>
          </div>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {description}
          </p>
        </div>
      </div>
      {!isCollapsed && <div className="p-4">{children}</div>}
    </section>
  );
}

function FieldLabel({ children }: { children: string }) {
  return (
    <p className="mb-1.5 text-[11px] font-semibold text-foreground">
      {children}
    </p>
  );
}

// ─── Portfolio projection panel ───────────────────────────────────────────────

type AfterHolding = {
  key: string;
  name: string;
  qty: number;
  price: number;
  marketValue: number;
  isEquity: boolean;
};

function PortfolioAfterPanel({
  portfolios,
  modelsData = [],
}: {
  portfolios: Portfolio[];
  modelsData?: QueryModelPosition[];
}) {
  const portfolioId = useWatch({ name: "dpPortfolioId" }) as string;
  const modelType = useWatch({ name: "dpModelType" }) as string;
  const modelTypeName = useWatch({ name: "dpModelTypeName" }) as string;
  const rawBuyOrders = useWatch({ name: "buyOrders" }) as BuyOrderRow[] | null;
  const rawSellOrders = useWatch({ name: "sellOrders" }) as
    | SellOrderRow[]
    | null;
  const amountToInvest =
    (useWatch({ name: "dpAmountToInvest" }) as number) ?? 0;
  const withdrawal = (useWatch({ name: "dpWithdrawal" }) as number) ?? 0;
  const sumInsValue = (useWatch({ name: "dpSumInsValue" }) as number) ?? 0;
  const sumAvCash = (useWatch({ name: "dpSumAvCash" }) as number) ?? 0;
  const managementFee = useWatch({ name: "dpManagementFee" }) as string;
  const subscriptionFeePct = useWatch({ name: "dpOnOffPercentage" }) as string;
  const dpAccount = useWatch({ name: "dpAccount" }) as string;
  const dpNewAccount = useWatch({ name: "dpNewAccount" }) as string;
  const bankAccount = dpAccount || dpNewAccount;

  const buyOrders = useMemo(() => rawBuyOrders ?? [], [rawBuyOrders]);
  const sellOrders = useMemo(() => rawSellOrders ?? [], [rawSellOrders]);

  const isNew = portfolioId === "NEW";
  const isNoModel = !modelType || modelType === "NOMODEL";
  const selectedPortfolio = portfolios.find(
    (p) => String(p.id) === String(portfolioId)
  );

  const currentItems = useMemo(
    () => selectedPortfolio?.portfolioReport?.portfolioReportItems ?? [],
    [selectedPortfolio]
  );
  const currentCash = selectedPortfolio?.portfolioReport?.accountBalance ?? 0;
  const currentMv = selectedPortfolio?.portfolioReport?.marketValue ?? 0;

  const currentEquityMv = currentItems
    .filter((i) => isEquityByClass1(i.security?.classType1?.code ?? ""))
    .reduce((s, i) => s + (i.marketValue ?? 0), 0);
  const currentEquityPct =
    currentMv > 0 ? (currentEquityMv / currentMv) * 100 : 0;

  // ── Model equity fraction ──────────────────────────────────────────────────
  const modelEquityFraction = useMemo(() => {
    if (isNoModel) return null;
    const positions = modelsData.filter((p) => p.short_name === modelType);
    if (positions.length > 0) {
      const total = positions.reduce((s, p) => s + p.share_of_portfolio, 0);
      const stockTotal = positions
        .filter((p) => isEquityByModelClass1(String(p.class_type1 ?? "")))
        .reduce((s, p) => s + p.share_of_portfolio, 0);
      return total > 0 ? stockTotal / total : 0;
    }
    return getModelEquityFractionFallback(modelType);
  }, [modelType, isNoModel, modelsData]);

  const modelDisplayName = useMemo(() => {
    if (!modelType || isNoModel) return null;
    const mapped: Record<string, string> = {
      CAPGUARDRENTER: "Aktiv Renter",
      CAPGUARDTRYGG: "Trygg",
      CAPGUARDEKB: "Aktiv EKB",
      CAPGUARDEIENDOM: "Aktiv Eiendom",
      FONDSOBLIGASJON: "Fondsobligasjoner",
      AKSJER: "Aktiv Aksjer",
      CAPGUARDAKSJER: "Aktiv Aksjer",
      CAPGUARD75: "Aktiv 75/25",
      CAPGUARD60: "Aktiv 60/40",
      CAPGUARD45: "Aktiv 45/55",
      CAPGUARD25: "Aktiv 25/75",
      NORDNET: "Nordnet",
    };
    return mapped[modelType] ?? modelTypeName ?? modelType;
  }, [modelType, modelTypeName, isNoModel]);

  // After-total for model orders
  const afterTotalModel = isNew
    ? amountToInvest
    : Math.max(0, sumInsValue + sumAvCash + amountToInvest - withdrawal);
  const afterEquityPctModel =
    modelEquityFraction != null ? modelEquityFraction * 100 : 0;

  // ── NOMODEL: holdings list ─────────────────────────────────────────────────
  const { afterHoldings, afterCash } = useMemo(() => {
    if (!isNoModel)
      return { afterHoldings: [] as AfterHolding[], afterCash: 0 };
    const map = new Map<string, AfterHolding>();
    for (const item of currentItems) {
      const key = String(item.security.id);
      map.set(key, {
        key,
        name: item.security.name,
        qty: item.amount,
        price: item.marketUnitPrice,
        marketValue: item.marketValue ?? 0,
        isEquity: isEquityByClass1(item.security?.classType1?.code ?? ""),
      });
    }
    for (const sell of sellOrders) {
      const qty = parseFloat(sell.sellQty) || 0;
      if (qty <= 0) continue;
      const key = String(sell.securityId);
      const ex = map.get(key);
      if (!ex) continue;
      const newQty = ex.qty - qty;
      if (newQty <= 1e-6) map.delete(key);
      else map.set(key, { ...ex, qty: newQty, marketValue: newQty * ex.price });
    }
    for (const buy of buyOrders) {
      if (!buy.instrumentName) continue;
      const amt = parseFloat(buy.amountInput) || 0;
      const price = parseFloat(buy.price) || 0;
      const fx = parseFloat(buy.fx) || 1;
      if (amt <= 0) continue;
      const newQty = price > 0 ? amt / (price * fx) : 0;
      const key = buy.securityId
        ? String(buy.securityId)
        : `buy_${buy.instrumentName}`;
      const ex = map.get(key);
      if (ex)
        map.set(key, {
          ...ex,
          qty: ex.qty + newQty,
          marketValue: ex.marketValue + amt,
        });
      else
        map.set(key, {
          key,
          name: buy.instrumentName,
          qty: newQty,
          price,
          marketValue: amt,
          isEquity: isEquityByClass1(buy.class1code),
        });
    }
    const totalBuyAmt = buyOrders.reduce(
      (s, r) => s + (parseFloat(r.amountInput) || 0),
      0
    );
    const totalSellAmt = sellOrders.reduce(
      (s, r) => s + (parseFloat(r.sellQty) || 0) * (r.marketUnitPrice ?? 0),
      0
    );
    const cash =
      currentCash + amountToInvest + totalSellAmt - totalBuyAmt - withdrawal;
    return {
      afterHoldings: Array.from(map.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
      afterCash: cash,
    };
  }, [
    isNoModel,
    currentItems,
    currentCash,
    amountToInvest,
    withdrawal,
    buyOrders,
    sellOrders,
  ]);

  const afterSecMv = afterHoldings.reduce((s, h) => s + h.marketValue, 0);
  const afterEquityMv = afterHoldings
    .filter((h) => h.isEquity)
    .reduce((s, h) => s + h.marketValue, 0);
  const afterTotalNoModel = afterSecMv + Math.max(afterCash, 0);
  const afterEquityPctNoModel =
    afterTotalNoModel > 0 ? (afterEquityMv / afterTotalNoModel) * 100 : 0;

  const hasChanges =
    buyOrders.some((r) => r.instrumentName && parseFloat(r.amountInput) > 0) ||
    sellOrders.some((r) => parseFloat(r.sellQty) > 0) ||
    amountToInvest > 0 ||
    withdrawal > 0;

  if (!selectedPortfolio && !isNew) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12 text-muted-foreground">
        <TrendingUp size={32} strokeWidth={1} className="opacity-25" />
        <p className="text-xs">Velg en portefølje for å se projeksjon</p>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  const afterTotal = isNoModel ? afterTotalNoModel : afterTotalModel;
  const afterEquityPct = isNoModel
    ? afterEquityPctNoModel
    : afterEquityPctModel;

  return (
    <div className="space-y-3">
      {/* Før / Etter summary */}
      {selectedPortfolio && (
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg bg-muted/40 px-3 py-2.5 space-y-1">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Før
            </p>
            <p className="text-sm tabular-nums font-semibold">
              {fmtAmt(currentMv)}
            </p>
            <p className="text-[11px] text-muted-foreground tabular-nums">
              Aksjer {formatNumber(currentEquityPct, 1)}%
            </p>
          </div>
          <div
            className={cn(
              "rounded-lg px-3 py-2.5 space-y-1 transition-colors",
              hasChanges
                ? "bg-blue-50 dark:bg-blue-950/30 ring-1 ring-blue-200 dark:ring-blue-800"
                : "bg-muted/40"
            )}
          >
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Etter
            </p>
            <p className="text-sm tabular-nums font-semibold">
              {fmtAmt(afterTotal)}
            </p>
            <p
              className={cn(
                "text-[11px] tabular-nums",
                hasChanges && afterEquityPct !== currentEquityPct
                  ? "font-semibold text-blue-700 dark:text-blue-300"
                  : "text-muted-foreground"
              )}
            >
              Aksjer {formatNumber(afterEquityPct, 1)}%
            </p>
          </div>
        </div>
      )}

      {/* Model summary */}
      {!isNoModel && modelEquityFraction != null && (
        <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
          {/* Model name header */}
          <div className="px-3 py-2 border-b border-border/40 flex items-center justify-between">
            <span className="text-[11px] font-semibold text-foreground">
              {modelDisplayName}
            </span>
            {amountToInvest > 0 && (
              <span className="text-[10px] tabular-nums text-muted-foreground">
                + {fmtAmt(amountToInvest)} kr
              </span>
            )}
          </div>
          {/* Allocation bar */}
          <div className="px-3 pt-3 pb-1">
            <div className="flex h-2.5 w-full rounded-full overflow-hidden bg-muted">
              <div
                className="bg-blue-500 transition-all duration-300"
                style={{ width: `${afterEquityPctModel}%` }}
              />
              <div
                className="bg-emerald-400 transition-all duration-300"
                style={{ width: `${100 - afterEquityPctModel}%` }}
              />
            </div>
          </div>
          {/* Stats */}
          <div className="grid grid-cols-2 divide-x divide-border/40 px-0 pb-2 pt-1">
            <div className="px-3 py-1">
              <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                Aksjer
              </p>
              <p className="text-sm font-bold tabular-nums text-blue-700 dark:text-blue-300">
                {formatNumber(afterEquityPctModel, 1)}%
              </p>
            </div>
            <div className="px-3 py-1">
              <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
                Renter
              </p>
              <p className="text-sm font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
                {formatNumber(100 - afterEquityPctModel, 1)}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* NOMODEL: holdings list */}
      {isNoModel && (afterHoldings.length > 0 || afterCash !== 0) && (
        <div className="rounded-lg border overflow-hidden">
          <div className="px-3 py-1.5 bg-muted/30 flex items-center justify-between border-b">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              Beholdning etter
            </span>
            <span className="text-[10px] tabular-nums text-muted-foreground">
              {fmtAmt(afterTotalNoModel)}
            </span>
          </div>
          <div className="divide-y max-h-72 overflow-y-auto">
            {afterHoldings.map((h) => {
              const pct =
                afterTotalNoModel > 0
                  ? (h.marketValue / afterTotalNoModel) * 100
                  : 0;
              return (
                <div
                  key={h.key}
                  className="flex items-center justify-between gap-3 px-3 py-1.5 hover:bg-muted/20"
                >
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    <span
                      className={cn(
                        "shrink-0 w-1.5 h-1.5 rounded-full",
                        h.isEquity ? "bg-blue-500" : "bg-emerald-400"
                      )}
                    />
                    <p className="text-xs truncate leading-tight">{h.name}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <span className="text-xs tabular-nums font-medium">
                      {fmtAmt(h.marketValue)}
                    </span>
                    <span className="text-[10px] text-muted-foreground tabular-nums ml-1.5">
                      {formatNumber(pct, 1)}%
                    </span>
                  </div>
                </div>
              );
            })}
            {afterCash !== 0 && (
              <div className="flex items-center justify-between px-3 py-1.5 text-xs text-muted-foreground">
                <span>Cash</span>
                <span
                  className={cn(
                    "tabular-nums font-medium",
                    afterCash < 0 && "text-destructive"
                  )}
                >
                  {fmtAmt(afterCash)}
                </span>
              </div>
            )}
          </div>
          <div className="px-3 py-1.5 border-t bg-muted/20 flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground">
              Total aksjeandel
            </span>
            <span
              className={cn(
                "text-xs tabular-nums font-bold",
                afterEquityPctNoModel > 0
                  ? "text-blue-700 dark:text-blue-300"
                  : "text-muted-foreground"
              )}
            >
              {formatNumber(afterEquityPctNoModel, 1)}%
            </span>
          </div>
        </div>
      )}

      {/* Order summary: fees + bank account */}
      {(managementFee || subscriptionFeePct || bankAccount) && (
        <div className="rounded-lg border border-border/60 overflow-hidden">
          <div className="px-3 py-1.5 bg-muted/30 border-b">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              Oppsummering
            </span>
          </div>
          <div className="divide-y divide-border/40">
            {managementFee && (
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-xs text-muted-foreground">
                  Forvaltningshonorar
                </span>
                <span className="text-xs font-semibold tabular-nums">
                  {managementFee === "1.5" ? (
                    <span className="text-amber-600 dark:text-amber-400">
                      Listepris
                    </span>
                  ) : (
                    `${managementFee} %`
                  )}
                </span>
              </div>
            )}
            {subscriptionFeePct !== undefined && subscriptionFeePct !== "" && (
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-xs text-muted-foreground">
                  Tegningsgebyr
                </span>
                <span className="text-xs font-semibold tabular-nums">
                  {subscriptionFeePct} %
                </span>
              </div>
            )}
            {bankAccount && (
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-xs text-muted-foreground">Bankkonto</span>
                <span className="text-xs font-semibold tabular-nums font-mono">
                  {bankAccount}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Inner form content (needs FormProvider context) ──────────────────────────

function OrderFormNewContent({
  portfolios,
  securities,
  isCompany,
  modelsData,
  documentId,
  onClose,
}: {
  portfolios: Portfolio[];
  securities: QuerySecurity[];
  isCompany: boolean;
  modelsData: QueryModelPosition[];
  documentId?: string;
  onClose?: () => void;
}) {
  const {
    register,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<OrderFormData>();

  const portfolioId = useWatch({ name: "dpPortfolioId" }) as string;
  const portType = useWatch({ name: "dpPortType" }) as string;
  const modelType = useWatch({ name: "dpModelType" }) as string;
  const bankAccount = useWatch({ name: "dpAccount" }) as string;
  const amountToInvest =
    (useWatch({ name: "dpAmountToInvest" }) as number) || 0;
  const sumAvCash = (useWatch({ name: "dpSumAvCash" }) as number) || 0;
  const sumInsValue = (useWatch({ name: "dpSumInsValue" }) as number) || 0;
  const managementFee = useWatch({ name: "dpManagementFee" }) as string;
  const subscriptionFeePct = useWatch({ name: "dpOnOffPercentage" }) as string;

  const isNew = portfolioId === "NEW";
  const isNoModel = modelType === "NOMODEL";
  const modelIsSet = !!modelType && !isNoModel;
  const totalPortValue = sumAvCash + sumInsValue + amountToInvest;
  const minThreshold = getMinimumMandateAmount(modelType);
  const showMinWarn =
    minThreshold > 0 && totalPortValue > 0 && totalPortValue < minThreshold;
  const selected = portfolios.find((p) => String(p.id) === String(portfolioId));
  const prevPortfolioIdRef = useRef(portfolioId);
  const [amountFocused, setAmountFocused] = useState(false);
  const [amountDisplay, setAmountDisplay] = useState("");

  // Sync formatted display whenever the form value changes externally (chips, portfolio load, etc.)
  useEffect(() => {
    if (!amountFocused) {
      setAmountDisplay(
        amountToInvest
          ? amountToInvest.toLocaleString("nb-NO", { maximumFractionDigits: 0 })
          : ""
      );
    }
  }, [amountToInvest, amountFocused]);

  useEffect(() => {
    if (portfolioId === prevPortfolioIdRef.current) return;
    prevPortfolioIdRef.current = portfolioId;
    if (selected) {
      setValue("dpPortType", selected.typeCode ?? "", { shouldValidate: true });
      setValue(
        "dpSumInsValue",
        selected.portfolioReport?.positionMarketValue ?? 0
      );
      setValue("dpPortfolioName", selected.name);
      setValue("dpSumAvCash", selected.portfolioReport?.accountBalance ?? 0);
      const linked = selected.modelPortfolio?.linkedPortfolio?.shortName;
      if (linked) {
        setValue("dpModelType", linked, { shouldValidate: true });
        setValue(
          "dpModelTypeName",
          selected.modelPortfolio?.linkedPortfolio?.name ??
            selected.modelPortfolio?.name ??
            ""
        );
      } else {
        setValue("dpModelType", "");
        setValue("dpModelTypeName", "");
      }
      const mgmtFee = selected.fees?.find((f) => f.type === "Management");
      setValue(
        "dpManagementFee",
        mgmtFee?.feePercent !== undefined ? String(mgmtFee.feePercent) : "1.5"
      );
      const karOk = selected.accounts?.find((a) =>
        a.tagsAsSet.includes("KAR-OK")
      );
      setValue("dpAccount", karOk?.number ?? "");
    } else if (isNew) {
      setValue("dpPortType", "");
      setValue("dpPortfolioName", "Ny portefølje");
      setValue("dpSumInsValue", 0);
      setValue("dpSumAvCash", 0);
      setValue("dpModelType", "");
      setValue("dpModelTypeName", "");
      setValue("dpManagementFee", "1.5");
      setValue("dpAccount", "");
      setValue("dpWithdrawal", 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId]);

  const existingModelValue =
    selected?.modelPortfolio?.linkedPortfolio?.shortName ?? "";
  const modelOptions = useMemo(() => {
    const base = getModelOptions(portType ?? "");
    return existingModelValue &&
      !base.find((o) => o.value === existingModelValue)
      ? [{ value: existingModelValue, label: existingModelValue }, ...base]
      : base;
  }, [portType, existingModelValue]);

  // Reset model if portType changes and current selection no longer valid
  useEffect(() => {
    if (
      modelType &&
      modelType !== existingModelValue &&
      !modelOptions.find((o) => o.value === modelType)
    ) {
      setValue("dpModelType", "");
      setValue("dpModelTypeName", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portType]);

  useEffect(() => {
    if (modelIsSet) setValue("dpFeeType", "all");
  }, [modelIsSet, setValue]);
  useEffect(() => {
    setValue("ordreModul", isNoModel ? "yes" : "no");
  }, [isNoModel, setValue]);
  useEffect(() => {
    if (!isNoModel) {
      setValue("buyOrders", []);
      setValue("sellOrders", []);
    }
  }, [isNoModel, setValue]);

  useEffect(() => {
    if (!showMinWarn) clearErrors("dpAmountToInvest");
  }, [clearErrors, showMinWarn]);

  const feeOptions = getManagementFeeOptions(
    selected?.fees?.find((f) => f.type === "Management")?.feePercent
  );

  const accountNumbers = useMemo(() => {
    const seen = new Set<string>();
    const nums: string[] = [];
    for (const p of portfolios) {
      for (const a of p.accounts ?? []) {
        if (a.tagsAsSet?.includes("KAR-OK") && !seen.has(a.number)) {
          seen.add(a.number);
          nums.push(a.number);
        }
      }
    }
    return nums;
  }, [portfolios]);

  const [priceListOpen, setPriceListOpen] = useState(false);

  const getModelEquityPct = (modelCode: string): number | null => {
    if (modelCode === "NOMODEL") return null;
    const positions = modelsData.filter(
      (position) => position.short_name === modelCode
    );
    if (positions.length > 0) {
      const total = positions.reduce(
        (sum, position) => sum + position.share_of_portfolio,
        0
      );
      const equity = positions
        .filter((position) =>
          isEquityByModelClass1(String(position.class_type1 ?? ""))
        )
        .reduce((sum, position) => sum + position.share_of_portfolio, 0);
      return total > 0 ? (equity / total) * 100 : 0;
    }
    return getModelEquityFractionFallback(modelCode) * 100;
  };

  return (
    <div className="min-h-full pb-4">
      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
        {/* ─ Left: form ─ */}
        <div className="min-w-0 space-y-4">
          {/* 1. Portfolio */}
          <FormSection
            number={1}
            icon={Building2}
            title="Portefølje"
            description="Velg hvilken portefølje ordren gjelder for."
          >
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <FieldLabel>Portefølje</FieldLabel>
                <select {...register("dpPortfolioId")} className={fieldCls}>
                  <option value="">— velg portefølje —</option>
                  <option value="NEW">Ny portefølje</option>
                  <option disabled>──────────</option>
                  {portfolios
                    .filter(
                      (p) =>
                        !isCompany || COMPANY_ALLOWED_PORT_TYPES.has(p.typeCode)
                    )
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.shortName ? `${p.shortName} · ` : ""}
                        {p.name}
                      </option>
                    ))}
                </select>
                {errors.dpPortfolioId && (
                  <p className={errorCls}>{errors.dpPortfolioId.message}</p>
                )}
              </div>
              <div>
                <FieldLabel>Type</FieldLabel>
                {isNew ? (
                  <select {...register("dpPortType")} className={fieldCls}>
                    <option value="">— velg type —</option>
                    {portfolioTypeOptions
                      .filter(
                        (o) =>
                          !isCompany || COMPANY_ALLOWED_PORT_TYPES.has(o.value)
                      )
                      .map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                  </select>
                ) : (
                  <input
                    value={
                      portfolioTypeOptions.find((o) => o.value === portType)
                        ?.label ??
                      portType ??
                      ""
                    }
                    readOnly
                    className={readonlyCls}
                  />
                )}
              </div>
            </div>

            {selected && (
              <div className="mt-4 overflow-hidden rounded-lg border border-border/60 bg-muted/20">
                <div className="flex items-center justify-between gap-3 border-b border-border/50 px-3 py-2.5">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Porteføljestatus
                    </p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      Nåværende verdi før ny investering
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                    Eksisterende
                  </span>
                </div>
                <dl className="grid grid-cols-1 divide-y divide-border/50 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                  <div className="px-3 py-2.5">
                    <dt className="text-[10px] text-muted-foreground">
                      Verdipapirer
                    </dt>
                    <dd className="mt-1 text-sm font-semibold tabular-nums text-foreground">
                      {fmtAmt(sumInsValue)}{" "}
                      <span className="text-xs font-normal text-muted-foreground">
                        kr
                      </span>
                    </dd>
                  </div>
                  <div className="px-3 py-2.5">
                    <dt className="text-[10px] text-muted-foreground">Cash</dt>
                    <dd className="mt-1 text-sm font-semibold tabular-nums text-foreground">
                      {fmtAmt(sumAvCash)}{" "}
                      <span className="text-xs font-normal text-muted-foreground">
                        kr
                      </span>
                    </dd>
                  </div>
                  <div className="bg-blue-50/50 px-3 py-2.5 dark:bg-blue-950/20">
                    <dt className="text-[10px] font-semibold text-blue-700 dark:text-blue-300">
                      Total porteføljeverdi
                    </dt>
                    <dd className="mt-1 text-base font-bold tabular-nums text-blue-800 dark:text-blue-200">
                      {fmtAmt(sumInsValue + sumAvCash)}{" "}
                      <span className="text-xs font-normal text-blue-700/70 dark:text-blue-300/70">
                        kr
                      </span>
                    </dd>
                  </div>
                </dl>
              </div>
            )}
          </FormSection>

          {/* 2. Strategy & Investment */}
          <FormSection
            number={2}
            icon={TrendingUp}
            title="Strategi og investering"
            description="Velg modell og angi beløpet som skal investeres."
          >
            {false && (
              <div className="space-y-4">
                <input type="hidden" {...register("dpModelType")} />
                <div>
                  <FieldLabel>Modellportefølje</FieldLabel>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {modelOptions.map((option) => {
                      const isSelectedModel = modelType === option.value;
                      const isManual = option.value === "NOMODEL";
                      const equityPct = getModelEquityPct(option.value);
                      const cardDescription = isManual
                        ? "Velg instrumenter og bygg ordren manuelt."
                        : "Ferdig modell for en samlet investeringsstrategi.";
                      return (
                        <button
                          key={option.value}
                          type="button"
                          disabled={!portType}
                          onClick={() => {
                            setValue("dpModelType", option.value, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                            setValue("dpModelTypeName", option.label);
                          }}
                          className={cn(
                            "group relative min-h-[104px] rounded-xl border p-3 text-left transition-all",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30",
                            !portType && "cursor-not-allowed opacity-50",
                            isSelectedModel
                              ? isManual
                                ? "border-blue-500 bg-blue-50/70 shadow-sm dark:border-blue-400 dark:bg-blue-950/30"
                                : "border-emerald-500 bg-emerald-50/70 shadow-sm dark:border-emerald-400 dark:bg-emerald-950/20"
                              : "border-border/70 bg-background hover:border-blue-300 hover:bg-blue-50/30 dark:hover:border-blue-700 dark:hover:bg-blue-950/20"
                          )}
                        >
                          <span
                            className={cn(
                              "absolute right-3 top-3 flex size-5 items-center justify-center rounded-full border-2",
                              isSelectedModel
                                ? isManual
                                  ? "border-blue-600 bg-blue-600 dark:border-blue-400 dark:bg-blue-400"
                                  : "border-emerald-600 bg-emerald-600 dark:border-emerald-400 dark:bg-emerald-400"
                                : "border-border bg-background"
                            )}
                          >
                            {isSelectedModel && (
                              <span className="size-1.5 rounded-full bg-white dark:bg-slate-950" />
                            )}
                          </span>
                          <span
                            className={cn(
                              "inline-flex rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider",
                              isManual
                                ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300"
                                : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                            )}
                          >
                            {isManual ? "Manuell" : "Modell"}
                          </span>
                          <p className="mt-3 pr-7 text-sm font-semibold text-foreground">
                            {option.label}
                          </p>
                          <p className="mt-1 max-w-[90%] text-[10px] leading-relaxed text-muted-foreground">
                            {cardDescription}
                          </p>
                          <div className="mt-3 pr-1">
                            <div className="mb-1 flex items-center justify-between text-[9px] font-medium text-muted-foreground">
                              <span>
                                {isManual
                                  ? "Aksjeandel"
                                  : "Aksjeandel i modellen"}
                              </span>
                              <span className="tabular-nums text-foreground">
                                {equityPct === null
                                  ? "Bestemmes av ordrene"
                                  : `${formatNumber(equityPct, 0)} %`}
                              </span>
                            </div>
                            <div className="relative h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                              {equityPct !== null && (
                                <div
                                  className={cn(
                                    "h-full rounded-full transition-all",
                                    isSelectedModel
                                      ? "bg-emerald-400"
                                      : "bg-emerald-300/70"
                                  )}
                                  style={{
                                    width: `${Math.max(
                                      0,
                                      Math.min(100, equityPct)
                                    )}%`,
                                  }}
                                />
                              )}
                              {equityPct === null && (
                                <div className="h-full w-full bg-[repeating-linear-gradient(135deg,transparent_0,transparent_4px,rgba(100,116,139,0.18)_4px,rgba(100,116,139,0.18)_6px)]" />
                              )}
                            </div>
                            <div className="mt-1 flex justify-between text-[8px] tabular-nums text-muted-foreground/70">
                              <span>0 %</span>
                              <span>50 %</span>
                              <span>100 %</span>
                            </div>
                          </div>
                          {isSelectedModel && (
                            <span
                              className={cn(
                                "absolute bottom-3 right-3 text-[9px] font-semibold",
                                isManual
                                  ? "text-blue-700 dark:text-blue-300"
                                  : "text-emerald-700 dark:text-emerald-300"
                              )}
                            >
                              Valgt
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {errors.dpModelType && (
                    <p className={errorCls}>{errors.dpModelType?.message}</p>
                  )}
                </div>

                <div>
                  <FieldLabel>Beløp å investere (NOK)</FieldLabel>
                  <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
                    <div>
                      {/* Shows formatted number when blurred and raw digits when focused. */}
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="0"
                        className={fieldCls}
                        value={amountDisplay}
                        onFocus={(e) => {
                          setAmountFocused(true);
                          setAmountDisplay(
                            amountToInvest ? String(amountToInvest) : ""
                          );
                          const el = e.target;
                          requestAnimationFrame(() => el.select());
                        }}
                        onChange={(e) => {
                          const raw = e.target.value
                            .replace(/[\s\u00a0]/g, "")
                            .replace(/^0+(?=\d)/, "");
                          setAmountDisplay(raw);
                          const num = raw === "" ? 0 : Number(raw);
                          if (!isNaN(num))
                            setValue("dpAmountToInvest", num, {
                              shouldValidate: true,
                            });
                        }}
                        onBlur={(e) => {
                          const raw = e.target.value.replace(/[\s\u00a0]/g, "");
                          const num = raw === "" ? 0 : Number(raw);
                          if (!isNaN(num))
                            setValue("dpAmountToInvest", num, {
                              shouldValidate: true,
                            });
                          setAmountFocused(false);
                          setAmountDisplay(
                            num
                              ? num.toLocaleString("nb-NO", {
                                  maximumFractionDigits: 0,
                                })
                              : ""
                          );
                        }}
                      />
                      {errors.dpAmountToInvest && (
                        <p className={errorCls}>
                          {errors.dpAmountToInvest?.message}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap items-start gap-1.5 sm:max-w-[330px]">
                      {[
                        { label: "10K", delta: 10_000 },
                        { label: "100K", delta: 100_000 },
                        { label: "500K", delta: 500_000 },
                        { label: "1M", delta: 1_000_000 },
                        { label: "5M", delta: 5_000_000 },
                      ].map(({ label, delta }) => (
                        <button
                          key={label}
                          type="button"
                          title={`Sett til ${label}`}
                          onClick={() =>
                            setValue("dpAmountToInvest", delta, {
                              shouldValidate: true,
                            })
                          }
                          className={cn(
                            "rounded-md border px-2.5 py-2 text-[10px] font-semibold tabular-nums transition-colors",
                            amountToInvest === delta
                              ? "border-blue-500 bg-blue-600 text-white"
                              : "border-border bg-background text-muted-foreground hover:border-blue-300 hover:bg-blue-50/40 hover:text-blue-700"
                          )}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Legacy select/input block retained only as historical context. */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel>Modellportefølje</FieldLabel>
                <select
                  {...register("dpModelType")}
                  disabled={!portType}
                  className={fieldCls}
                >
                  <option value="">— velg modell —</option>
                  {modelOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {errors.dpModelType && (
                  <p className={errorCls}>{errors.dpModelType?.message}</p>
                )}
              </div>
              <div>
                <FieldLabel>Beløp å investere</FieldLabel>
                {/* Shows formatted number (e.g. "1 500 000") when blurred, raw digits when focused */}
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="0"
                  className={fieldCls}
                  value={amountDisplay}
                  onFocus={(e) => {
                    setAmountFocused(true);
                    setAmountDisplay(
                      amountToInvest ? String(amountToInvest) : ""
                    );
                    // select after React re-renders the raw value
                    const el = e.target;
                    requestAnimationFrame(() => el.select());
                  }}
                  onChange={(e) => {
                    const raw = e.target.value
                      .replace(/[\s\u00a0]/g, "")
                      .replace(/^0+(?=\d)/, "");
                    setAmountDisplay(raw);
                    const num = raw === "" ? 0 : Number(raw);
                    if (!isNaN(num))
                      setValue("dpAmountToInvest", num, {
                        shouldValidate: true,
                      });
                  }}
                  onBlur={(e) => {
                    const raw = e.target.value.replace(/[\s\u00a0]/g, "");
                    const num = raw === "" ? 0 : Number(raw);
                    if (!isNaN(num))
                      setValue("dpAmountToInvest", num, {
                        shouldValidate: true,
                      });
                    setAmountFocused(false);
                    setAmountDisplay(
                      num
                        ? num.toLocaleString("nb-NO", {
                            maximumFractionDigits: 0,
                          })
                        : ""
                    );
                  }}
                />
                {errors.dpAmountToInvest && (
                  <p className={errorCls}>{errors.dpAmountToInvest?.message}</p>
                )}
                {/* Quick-amount chips: + adds, label sets directly, − subtracts */}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {[
                    { label: "10K", delta: 10_000 },
                    { label: "100K", delta: 100_000 },
                    { label: "500K", delta: 500_000 },
                    { label: "1M", delta: 1_000_000 },
                    { label: "5M", delta: 5_000_000 },
                  ].map(({ label, delta }) => {
                    const isActive = amountToInvest === delta;
                    return (
                      <div
                        key={label}
                        className={cn(
                          "flex items-center h-7 rounded-lg overflow-hidden text-[11px] font-bold shadow-sm ring-1 transition-all",
                          isActive
                            ? "ring-blue-500 shadow-blue-200 dark:shadow-blue-900"
                            : "ring-gray-200 dark:ring-gray-700"
                        )}
                      >
                        <button
                          type="button"
                          title={`+${label}`}
                          onClick={() =>
                            setValue(
                              "dpAmountToInvest",
                              Math.max(0, (amountToInvest || 0) + delta),
                              { shouldValidate: true }
                            )
                          }
                          className="h-full px-1.5 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors border-r border-gray-200 dark:border-gray-700"
                        >
                          +
                        </button>
                        <button
                          type="button"
                          title={`Sett til ${label}`}
                          onClick={() =>
                            setValue("dpAmountToInvest", delta, {
                              shouldValidate: true,
                            })
                          }
                          className={cn(
                            "h-full px-2.5 tabular-nums transition-colors",
                            isActive
                              ? "bg-blue-600 text-white"
                              : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-950/40 hover:text-blue-700 dark:hover:text-blue-300"
                          )}
                        >
                          {label}
                        </button>
                        <button
                          type="button"
                          title={`−${label}`}
                          onClick={() =>
                            setValue(
                              "dpAmountToInvest",
                              Math.max(0, (amountToInvest || 0) - delta),
                              { shouldValidate: true }
                            )
                          }
                          className="h-full px-1.5 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 hover:text-rose-500 dark:hover:text-rose-400 transition-colors border-l border-gray-200 dark:border-gray-700"
                        >
                          −
                        </button>
                      </div>
                    );
                  })}
                  {amountToInvest > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setValue("dpAmountToInvest", 0, {
                          shouldValidate: true,
                        })
                      }
                      className="px-2.5 py-1 rounded-lg text-[11px] font-bold shadow-sm ring-1 ring-gray-200 dark:ring-gray-700 bg-white dark:bg-gray-900 text-gray-400 dark:text-gray-500 hover:ring-rose-300 dark:hover:ring-rose-800 hover:bg-rose-50 dark:hover:bg-rose-950/30 hover:text-rose-500 dark:hover:text-rose-400 transition-all"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </div>

            {showMinWarn && (
              <div className="flex gap-2.5 rounded-lg border border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-950/40 px-3 py-2.5">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500 mt-0.5" />
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  {modelType === "CAPGUARDEKB" ? "Aktiv EKB" : "Aktiv Eiendom"}{" "}
                  krever min.{" "}
                  <span className="font-semibold">
                    {fmtAmt(minThreshold)} kr
                  </span>{" "}
                  — mangler{" "}
                  <span className="font-semibold">
                    {fmtAmt(minThreshold - totalPortValue)} kr
                  </span>
                  .
                </p>
              </div>
            )}
          </FormSection>

          {/* 3. Fees */}
          <FormSection
            number={3}
            icon={BadgePercent}
            title="Gebyrer og forvaltning"
            description="Angi tegningsgebyr og forvaltningshonorar."
          >
            <div className="grid grid-cols-3 gap-3 items-end">
              <div>
                <FieldLabel>Tegningsgebyr %</FieldLabel>
                <input
                  type="text"
                  {...register("dpOnOffPercentage")}
                  placeholder="0"
                  className={fieldCls}
                  onBlur={(e) => {
                    const cleaned = e.target.value.replace(/^0+(?=\d)/, "");
                    if (cleaned !== e.target.value)
                      setValue("dpOnOffPercentage", cleaned);
                  }}
                />
                {errors.dpOnOffPercentage && (
                  <p className={errorCls}>{errors.dpOnOffPercentage.message}</p>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <FieldLabel>Forvaltningshonorar</FieldLabel>
                  <button
                    type="button"
                    title="Vis prisliste"
                    onClick={() => setPriceListOpen((v) => !v)}
                    className={cn(
                      "flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide transition-colors",
                      priceListOpen
                        ? "bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300"
                        : "text-muted-foreground hover:text-violet-600 dark:hover:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-950/30"
                    )}
                  >
                    <BadgePercent size={10} />
                    Priser
                  </button>
                </div>
                <select {...register("dpManagementFee")} className={fieldCls}>
                  {feeOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <FieldLabel>Fee type</FieldLabel>
                <div className="flex gap-4 pt-1.5">
                  {(["onlyFund", "all"] as const).map((val) => (
                    <label
                      key={val}
                      className={cn(
                        "flex items-center gap-1.5 text-xs select-none",
                        modelIsSet
                          ? "opacity-40 cursor-not-allowed"
                          : "cursor-pointer"
                      )}
                    >
                      <input
                        type="radio"
                        value={val}
                        disabled={modelIsSet}
                        {...register("dpFeeType")}
                        className="accent-blue-600"
                      />
                      {val === "onlyFund" ? "Only fund" : "All"}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </FormSection>

          {/* 4. Settlement */}
          <FormSection
            number={4}
            icon={Landmark}
            title="Finansiering"
            description="Velg konto og eventuelt uttak."
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel>Bankkonto</FieldLabel>
                <select
                  {...register("dpAccount")}
                  className={cn(
                    fieldCls,
                    errors.dpAccount && "border-destructive"
                  )}
                >
                  <option value="">— velg konto —</option>
                  <option value="newAccount">Ny konto</option>
                  <option disabled>──────────</option>
                  {accountNumbers.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                {errors.dpAccount && (
                  <p className={errorCls}>{errors.dpAccount.message}</p>
                )}
              </div>
              {!isNew && (
                <div>
                  <FieldLabel>Uttak</FieldLabel>
                  <input
                    type="number"
                    min={0}
                    step={1000}
                    {...register("dpWithdrawal", { valueAsNumber: true })}
                    placeholder="0"
                    className={fieldCls}
                  />
                </div>
              )}
            </div>
            {bankAccount === "newAccount" && (
              <div>
                <FieldLabel>Nytt kontonummer (11 siffer)</FieldLabel>
                <input
                  type="text"
                  {...register("dpNewAccount", {
                    validate: (v) =>
                      !v || validateAccountNumber(v) || "Ugyldig kontonummer",
                  })}
                  placeholder="XXXXXXXXXXX"
                  className={cn(
                    fieldCls,
                    errors.dpNewAccount && "border-destructive"
                  )}
                />
                {errors.dpNewAccount && (
                  <p className={errorCls}>{errors.dpNewAccount.message}</p>
                )}
              </div>
            )}
          </FormSection>

          {/* 5. Manual orders (NOMODEL only) */}
          <FormSection
            number={5}
            icon={ListChecks}
            title="Ordredetaljer"
            description={
              isNoModel
                ? "Legg til eventuelle kjøp og salg for ordren."
                : "Velg No Model under modell for å legge inn kjøp og salg."
            }
            disabled={!isNoModel}
            collapsed={!isNoModel}
            className="space-y-3"
          >
            <OrdreModulSection
              portfolios={portfolios}
              securities={securities}
            />
          </FormSection>

          {/* 6. Agreements */}
          <FormSection
            number={6}
            icon={FileCheck2}
            title="Avtaler"
            description="Velg hvilke avtaler som skal inkluderes i dokumentproduksjonen."
          >
            <AgreementsGroup
              securities={securities}
              allPortfolios={portfolios}
            />
          </FormSection>
        </div>

        {/* ─ Right: summary and projection ─ */}
        <aside className="min-w-0 xl:sticky xl:top-0">
          <div className="space-y-4">
            <div className="overflow-hidden rounded-xl border border-border/70 bg-card shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
              <div className="flex items-center gap-3 border-b border-border/50 px-4 py-3">
                <div className="flex size-8 items-center justify-center rounded-full bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-300">
                  <WalletCards className="size-4" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold">Sammendrag</h2>
                  <p className="text-[11px] text-muted-foreground">
                    Oppsummering av valgene dine.
                  </p>
                </div>
              </div>
              <div className="px-4 py-3">
                <div className="space-y-2 text-xs">
                  {[
                    {
                      label: "Portefølje",
                      value:
                        selected?.name ||
                        (portfolioId === "NEW" ? "New portfolio" : "—"),
                    },
                    {
                      label: "Type",
                      value:
                        portfolioTypeOptions.find((o) => o.value === portType)
                          ?.label || "—",
                    },
                    {
                      label: "Modellportefølje",
                      value:
                        modelOptions.find((o) => o.value === modelType)
                          ?.label || "—",
                    },
                    {
                      label: "Beløp å investere",
                      value: amountToInvest
                        ? `${fmtAmt(amountToInvest)} kr`
                        : "—",
                    },
                    {
                      label: "Tegningsgebyr",
                      value: `${subscriptionFeePct || "0"} %`,
                    },
                    {
                      label: "Forvaltningshonorar",
                      value:
                        managementFee === "1.5"
                          ? "Listepris"
                          : `${managementFee || "—"} %`,
                    },
                    { label: "Fee type", value: modelIsSet ? "All" : "—" },
                    { label: "Bankkonto", value: bankAccount || "—" },
                  ].map(({ label, value }) => (
                    <div
                      key={label}
                      className="flex items-center justify-between gap-3 border-b border-border/40 pb-2 last:border-0 last:pb-0"
                    >
                      <span className="text-muted-foreground">{label}</span>
                      <span className="max-w-[58%] truncate text-right font-medium tabular-nums text-foreground">
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-border/70 bg-card p-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
              <div className="mb-3 flex items-center gap-2 border-b border-border/40 pb-2">
                <TrendingUp size={14} className="text-blue-500" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  Porteføljeprojeksjon
                </span>
              </div>
              <PortfolioAfterPanel
                portfolios={portfolios}
                modelsData={modelsData}
              />
              <PriceTablePanel
                open={priceListOpen}
                onToggle={() => setPriceListOpen((v) => !v)}
                amountToInvest={amountToInvest}
                modelType={modelType}
                managementFee={managementFee}
              />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

// ─── PriceTablePanel ──────────────────────────────────────────────────────────

function PriceTablePanel({
  open,
  onToggle,
  amountToInvest = 0,
  modelType = "",
  managementFee = "",
}: {
  open: boolean;
  onToggle: () => void;
  amountToInvest?: number;
  modelType?: string;
  managementFee?: string;
}) {
  const mnok = amountToInvest / 1_000_000;
  const activeTier =
    mnok >= 10
      ? "10+"
      : mnok >= 5
      ? "5-10"
      : mnok >= 2
      ? "2-5"
      : amountToInvest > 0
      ? "<2"
      : null;

  // Map model code to the price-table product row key
  const modelToKey: Record<
    string,
    "active" | "ekb" | "realEstate" | "bonds" | "safe"
  > = {
    CAPGUARD75: "active",
    CAPGUARD60: "active",
    CAPGUARD45: "active",
    CAPGUARD25: "active",
    CAPGUARDAKSJER: "active",
    AKSJER: "active",
    CAPGUARDEKB: "ekb",
    CAPGUARDEIENDOM: "realEstate",
    CAPGUARDRENTER: "bonds",
    FONDSOBLIGASJON: "bonds",
    CAPGUARDTRYGG: "safe",
  };
  const activeProductKey = modelType ? modelToKey[modelType] ?? null : null;
  // Transposed: products as rows, tiers as columns — fits narrow sidebar without horizontal scroll.
  const products = [
    {
      label: "Aktiv",
      portfolios: [
        "CAPGUARDAKSJER",
        "CAPGUARD25",
        "CAPGUARD45",
        "CAPGUARD60",
        "CAPGUARD75",
      ],
      key: "active" as const,
    },
    { label: "EKB", portfolios: ["CAPGUARDEKB"], key: "ekb" as const },
    {
      label: "Eiendom",
      portfolios: ["CAPGUARDEIENDOM"],
      key: "realEstate" as const,
    },
    { label: "Renter", portfolios: ["CAPGUARDRENTER"], key: "bonds" as const },
    { label: "Trygg", portfolios: ["CAPGUARDTRYGG"], key: "safe" as const },
  ];
  // Only the MNOK fee rows (skip boolean / depot rows in this compact view)
  const feeRows = PRICELIST_ROWS.filter(
    (r) =>
      r.tier !== "Kurtasje belastes" &&
      r.tier !== "Minstetegning" &&
      r.tier !== "Depot" &&
      r.tier !== "Inn/Ut"
  );
  const tiers = feeRows.map((r) => r.tier);

  return (
    <div
      className={cn(
        "rounded-lg border overflow-hidden transition-colors",
        open ? "border-violet-300 dark:border-violet-700" : "border-border/40"
      )}
    >
      <button
        type="button"
        onClick={() => onToggle()}
        className={cn(
          "w-full flex items-center justify-between px-3 py-2 transition-colors",
          open
            ? "bg-violet-100 dark:bg-violet-900/40 hover:bg-violet-200 dark:hover:bg-violet-800/50"
            : "hover:bg-muted/30"
        )}
      >
        <div className="flex items-center gap-1.5">
          <BadgePercent
            size={11}
            className={
              open ? "text-violet-600 dark:text-violet-400" : "text-violet-500"
            }
          />
          <span
            className={cn(
              "text-[10px] font-bold uppercase tracking-widest",
              open
                ? "text-violet-700 dark:text-violet-300"
                : "text-muted-foreground"
            )}
          >
            Prisliste
          </span>
        </div>
        <ChevronDown
          size={12}
          className={cn(
            "transition-transform duration-200",
            open
              ? "text-violet-600 dark:text-violet-400 rotate-180"
              : "text-muted-foreground"
          )}
        />
      </button>
      {open && (
        <div className="border-t border-border/40">
          <table className="w-full text-[10px]">
            <thead>
              <tr className="bg-muted/30">
                <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground"></th>
                {tiers.map((t) => (
                  <th
                    key={t}
                    className="px-2 py-1.5 text-right font-semibold text-muted-foreground tabular-nums"
                  >
                    {t}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {products.map(({ label, key }) => (
                <tr key={label} className="hover:bg-muted/20">
                  <td className="px-2 py-1.5 font-semibold text-foreground whitespace-nowrap">
                    {label}
                  </td>
                  {feeRows.map((row) => {
                    const isActive =
                      managementFee === "1.5" &&
                      activeProductKey === key &&
                      row.tier === activeTier;
                    return (
                      <td
                        key={row.tier}
                        className={cn(
                          "px-2 py-1.5 text-right tabular-nums font-medium",
                          isActive
                            ? "bg-violet-500 text-white rounded"
                            : "text-foreground"
                        )}
                      >
                        {row[key]}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── buildExistData ────────────────────────────────────────────────────────────

function buildExistData(portfolio: Portfolio | undefined) {
  if (!portfolio) return { isExisting: false };
  const managementFee = portfolio.fees?.find((f) => f.type === "Management");
  const karOkAccount = portfolio.accounts?.find((a) =>
    a.tagsAsSet.includes("KAR-OK")
  );
  return {
    dpPortfolioId: String(portfolio.id),
    dpPortfolioName: portfolio.name,
    dpPortfolioShortName: portfolio.shortName ?? "",
    dpPortType: portfolio.typeCode ?? "",
    dpModelType:
      portfolio.modelPortfolio?.linkedPortfolio?.shortName ??
      portfolio.modelPortfolio?.code ??
      "",
    dpModelTypeName:
      portfolio.modelPortfolio?.linkedPortfolio?.name ??
      portfolio.modelPortfolio?.name ??
      "",
    dpAmountToInvest: 0,
    dpSumAvCash: portfolio.portfolioReport?.accountBalance ?? 0,
    dpSumInsValue: portfolio.portfolioReport?.positionMarketValue ?? 0,
    dpOnOffPercentage: "0",
    dpManagementFee:
      managementFee?.feePercent !== undefined
        ? String(managementFee.feePercent)
        : "1.5",
    dpFeeType: "all",
    dpOnOffAmount: 0,
    dpOnOffDate: "",
    dpAccount: karOkAccount?.number ?? "",
    dpNewAccount: "",
    dpWithdrawal: 0,
    ordreModul: "no",
    buyOrders: [],
    sellOrders: [],
    isExisting: true,
  };
}

// ─── Exported form ─────────────────────────────────────────────────────────────

export type OrderFormNewProps = {
  portfolios: Portfolio[];
  isCompany?: boolean;
  securities?: QuerySecurity[];
  modelsData?: QueryModelPosition[];
  initialData?: Record<string, unknown>;
  documentId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDocumentCreated?: (data: Record<string, any>, documentId?: string) => void;
  onClose?: () => void;
};

export function OrderFormNew({
  portfolios,
  isCompany = false,
  securities = [],
  modelsData = [],
  initialData,
  documentId,
  onDocumentCreated,
  onClose,
}: OrderFormNewProps) {
  const form = useOrderForm(initialData as Parameters<typeof useOrderForm>[0]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = (data: Record<string, any>) => {
    const minThreshold = getMinimumMandateAmount(data.dpModelType);
    const totalPortValue =
      (Number(data.dpSumAvCash) || 0) +
      (Number(data.dpSumInsValue) || 0) +
      (Number(data.dpAmountToInvest) || 0);
    if (
      minThreshold > 0 &&
      totalPortValue > 0 &&
      totalPortValue < minThreshold
    ) {
      form.setError("dpAmountToInvest", {
        type: "manual",
        message: `${
          data.dpModelType === "CAPGUARDEKB" ? "Aktiv EKB" : "Aktiv Eiendom"
        } krever en minimumsverdi på ${fmtAmt(minThreshold)} kr.`,
      });
      return;
    }
    form.clearErrors("dpAmountToInvest");

    if (data.dpModelType === "NOMODEL" && data.dpAmountToInvest > 0) {
      const buyRows = (data.buyOrders ?? []) as Array<{
        instrumentName?: string;
        amountInput?: string;
      }>;
      const sumBuy = buyRows
        .filter((r) => r.instrumentName)
        .reduce((s, r) => s + (parseFloat(r.amountInput ?? "") || 0), 0);
      if (Math.abs(sumBuy - data.dpAmountToInvest) > 0.01) {
        form.setError("buyOrders", {
          type: "manual",
          message: `Kjøpsordre totalt (${fmtAmt(
            sumBuy
          )}) må tilsvare investeringsbeløpet (${fmtAmt(
            data.dpAmountToInvest
          )})`,
        });
        return;
      }
      form.clearErrors("buyOrders");
    }
    const selectedPortfolio = portfolios.find(
      (p) => p.id === Number(data.dpPortfolioId)
    );
    onDocumentCreated?.(
      { existData: buildExistData(selectedPortfolio), newData: data },
      documentId
    );
    onClose?.();
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <OrderFormNewContent
          portfolios={portfolios}
          securities={securities}
          isCompany={isCompany}
          modelsData={modelsData}
          documentId={documentId}
          onClose={onClose}
        />
        <div className="sticky bottom-0 -mx-4 px-4 py-3 border-t border-border bg-background/95 backdrop-blur flex justify-end gap-3 z-10">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md border border-border text-sm font-medium hover:bg-muted transition-colors"
            >
              Avbryt
            </button>
          )}
          <button
            type="submit"
            className="px-5 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-sm transition-colors"
          >
            {documentId ? "Lagre endringer" : "Legg til dokument"}
          </button>
        </div>
      </form>
    </FormProvider>
  );
}
